import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * IconCircle — tinted circular badge wrapping an icon. Pencil JVvLH bCiEo XNhqQ WsKLu.
 * Used in empty states, onboarding checklists, stat cards, connection rows.
 */
export const iconCircleVariants = cva(
  'inline-flex items-center justify-center rounded-pill shrink-0',
  {
    variants: {
      tone: {
        neutral: 'bg-ia-elevated text-ia-text',
        brand: 'bg-ia-brand-muted text-ia-text-link',
        success: 'bg-ia-success-bg text-ia-success',
        warning: 'bg-ia-warning-bg text-ia-warning',
        error: 'bg-ia-error-bg text-ia-error',
        info: 'bg-ia-info-bg text-ia-info',
      },
      size: {
        sm: 'h-7 w-7 [&_svg]:size-3.5',
        md: 'h-10 w-10 [&_svg]:size-5',
        lg: 'h-12 w-12 [&_svg]:size-6',
        xl: 'h-16 w-16 [&_svg]:size-8',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
)

export interface IconCircleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof iconCircleVariants> {
  icon: LucideIcon
}

export const IconCircle = React.forwardRef<HTMLDivElement, IconCircleProps>(
  ({ className, tone, size, icon, ...props }, ref) => (
    <div ref={ref} className={cn(iconCircleVariants({ tone, size }), className)} {...props}>
      <Icon icon={icon} />
    </div>
  ),
)
IconCircle.displayName = 'IconCircle'
